import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('cz:auth_error')
      if (msg) {
        setError(msg)
        sessionStorage.removeItem('cz:auth_error')
      }
    } catch { /* noop */ }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!email || !senha) { setError('Preencha e-mail e senha'); return }

    setLoading(true)
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email, password: senha,
      })
      if (authError) {
        setError(authError.message === 'Invalid login credentials'
          ? 'E-mail ou senha incorretos'
          : authError.message)
        return
      }
      if (data?.user) {
        // Check profile status before allowing access
        const { data: profile } = await supabase
          .from('profiles')
          .select('status')
          .eq('id', data.user.id)
          .single()

        if (profile?.status && profile.status !== 'aprovado') {
          await supabase.auth.signOut()
          setError('Seu acesso ainda não foi liberado. Entre em contato com o administrador.')
          return
        }

        // Verifica se a empresa do usuário está ativa
        const { data: profileCompany } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', data.user.id)
          .single()

        if (profileCompany?.company_id) {
          const { data: company } = await supabase
            .from('companies')
            .select('is_active')
            .eq('id', profileCompany.company_id)
            .single()

          if (company && company.is_active === false) {
            await supabase.auth.signOut()
            setError('Sua empresa está aguardando aprovação. Entre em contato com o administrador.')
            return
          }
        }

        setTimeout(() => window.location.reload(), 0)
      }
    } catch (err: any) {
      setError('Erro: ' + (err.message || 'tente novamente'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center p-5 z-[9000]"
      style={{
        background: 'hsl(var(--bg0))',
        backgroundImage: `
          radial-gradient(ellipse 70% 50% at 50% -10%, hsl(var(--amber) / 0.14), transparent),
          radial-gradient(ellipse 50% 40% at 80% 110%, hsl(var(--sky) / 0.07), transparent)
        `,
      }}
    >
      <div className="w-full max-w-[390px] animate-fade-up">
        {/* Logo */}
        <div className="text-center mb-7">
          <img src="/construzilla-logo.png?v=6" alt="Construzilla" className="inline-block object-contain mb-2" style={{ maxWidth: 320, background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 }} />
          <div className="text-xs text-muted-foreground tracking-wide">
            Gestão inteligente de obras · v5.0
          </div>
        </div>

        {/* Card */}
        <form onSubmit={handleLogin}
          className="rounded-3xl p-7 border"
          style={{
            background: 'hsl(var(--bg2))',
            borderColor: 'hsl(var(--line2))',
            boxShadow: '0 8px 32px rgba(0,0,0,.6)',
          }}
        >
          <div className="text-[17px] font-bold text-foreground mb-1">Acessar o sistema</div>
          <div className="text-xs text-muted-foreground mb-5 leading-relaxed">
            Entre com seu e-mail e senha cadastrados
          </div>

          {error && (
            <div className="mb-3.5 px-3 py-2.5 rounded-lg text-xs font-medium leading-snug"
              style={{
                background: 'hsl(var(--rose-bg))',
                color: 'hsl(var(--rose))',
                border: '1px solid hsl(var(--rose) / 0.25)',
              }}
            >
              {error}
            </div>
          )}

          <div className="mb-3">
            <label className="block text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">
              E-mail
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="seu@email.com"
              autoComplete="email"
              className="w-full rounded-[10px] px-3.5 py-2.5 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground"
              style={{
                background: 'hsl(var(--bg3))',
                border: '1.5px solid hsl(var(--line2))',
              }}
              onFocus={e => { e.target.style.borderColor = 'hsl(var(--amber))'; e.target.style.boxShadow = '0 0 0 3px hsl(var(--amber) / 0.12)' }}
              onBlur={e => { e.target.style.borderColor = 'hsl(var(--line2))'; e.target.style.boxShadow = 'none' }}
            />
          </div>

          <div className="mb-5">
            <label className="block text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">
              Senha
            </label>
            <input
              type="password"
              value={senha}
              onChange={e => setSenha(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              className="w-full rounded-[10px] px-3.5 py-2.5 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground"
              style={{
                background: 'hsl(var(--bg3))',
                border: '1.5px solid hsl(var(--line2))',
              }}
              onFocus={e => { e.target.style.borderColor = 'hsl(var(--amber))'; e.target.style.boxShadow = '0 0 0 3px hsl(var(--amber) / 0.12)' }}
              onBlur={e => { e.target.style.borderColor = 'hsl(var(--line2))'; e.target.style.boxShadow = 'none' }}
            />
          </div>

          <div className="text-right mb-4">
            <Link to="/forgot-password" className="text-[11px] text-primary hover:underline font-medium">
              Esqueci minha senha
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl mt-1 bg-primary text-primary-foreground text-sm font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-90 active:scale-[0.99]"
            style={{ boxShadow: '0 4px 16px hsl(var(--amber) / 0.3)', letterSpacing: '0.01em' }}
          >
            {loading ? 'Entrando...' : 'Entrar →'}
          </button>
        </form>

        <div className="text-center text-[11px] text-muted-foreground mt-3.5 space-y-2">
          <div>🔒 Dados sincronizados na nuvem</div>
          <div>
            <Link to="/solicitar-acesso" className="text-primary hover:underline font-medium">
              Solicitar acesso
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
